import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { Button, Input, Modal } from 'semantic-ui-react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import Swal from 'sweetalert2';
import crecerFormsInstance from 'src/services/api/crecer';
import EventService from 'src/services/api/eventService';
import {
  CrecerReportContainer,
  crecerSteps,
  seminaryEvent,
} from 'src/containers/crecer/crecerShared';
import {
  isCaptchaRequired,
  useRecaptchaToken,
  RECAPTCHA_SECURITY_ERROR,
} from 'src/components/helpers/recaptchaUtils';

const crecerStepToEvent = {
  conectar: 'crecerConectar',
  encuentro: 'crecerEncuentro',
  vida: 'crecerVida',
  influencia: 'crecerInfluence',
  bautizo: 'crecerBautizo',
};

const PageContainer = styled(CrecerReportContainer)`
  .info-row {
    margin: 10px 0;
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.95);
    &.title {
      font-size: 1.2rem;
      font-weight: 600;
      margin-bottom: 10px;
      color: #fff;
    }
  }

  .info-label {
    font-weight: 600;
    margin-right: 8px;
  }

  .identification-section {
    margin-top: 24px;
    width: 100%;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
    background-color: transparent !important;

    label {
      color: #fff !important;
      display: block;
      margin-bottom: 8px;
    }

    .ui.input,
    .ui.input input {
      background: #fff !important;
      color: #1a1a1a !important;
      border: 2px solid rgba(255, 255, 255, 0.5) !important;
      border-radius: 12px;
    }

    .ui.input input::placeholder {
      color: #666;
    }

    .ui.button {
      margin-top: 16px;
      background: linear-gradient(45deg, #4caf50, #45a049) !important;
      color: #fff !important;
      border: none !important;
    }
  }
`;

const CrecerSelfAttendance = () => {
  const location = useLocation();
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [identification, setIdentification] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [confirmedIdentification, setConfirmedIdentification] = useState('');
  const [eventDates, setEventDates] = useState([]);
  const [checkingIdentity, setCheckingIdentity] = useState(false);
  const [previewName, setPreviewName] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const getPreviewCaptchaToken = useRecaptchaToken('crecer_self_attendance');
  const getConfirmCaptchaToken = useRecaptchaToken('crecer_self_attendance_confirm');

  useEffect(() => {
    const params = queryString.parse(location.search);
    const t = params.token?.trim();
    if (!t) {
      setError('Falta el token en la URL. Usa el enlace del código QR.');
      setLoading(false);
      return;
    }
    setToken(t);
    crecerFormsInstance
      .getSelfAttendanceByToken({ token: t })
      .then((response) => {
        const data = response?.data?.data ?? response?.data;
        if (data && (data.type || data.class || data.date || data.start_date || data.startDate)) {
          setInfo(data);
        } else {
          setError('Token no válido o expirado.');
        }
        setLoading(false);
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || 'Token no válido o expirado.';
        setError(msg);
        setLoading(false);
      });
  }, [location.search]);

  useEffect(() => {
    EventService.getDates()
      .then((response) => {
        if (response?.data?.success && response.data.dates) {
          setEventDates(response.data.dates);
        }
      })
      .catch(() => {});
  }, []);

  const handleIdentificationChange = (e, { value }) => {
    setIdentification(value || '');
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewName('');
  };

  // Show whose attendance is about to be written before writing anything, so
  // it can no longer land silently on somebody else's account.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) return;
    const idTrimmed = identification.trim();
    if (!idTrimmed) {
      Swal.fire('Ingresa tu número de identificación.');
      return;
    }

    const captchaRequired = isCaptchaRequired();
    const captcha = await getPreviewCaptchaToken({ skip: !captchaRequired });
    if (captchaRequired && !captcha) {
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      return;
    }

    setCheckingIdentity(true);
    crecerFormsInstance
      .getSelfAttendanceGuestPreview({
        token,
        identification: idTrimmed,
        ...(captchaRequired ? { captcha } : {}),
      })
      .then((response) => {
        setCheckingIdentity(false);
        setPreviewName(response?.data?.name || '');
        setPreviewOpen(true);
      })
      .catch((err) => {
        setCheckingIdentity(false);
        Swal.fire(err?.response?.data?.message || 'No pudimos verificar tu identificación.');
      });
  };

  const confirmAttendance = async () => {
    const idTrimmed = identification.trim();
    if (!token || !idTrimmed) return;

    const captchaRequired = isCaptchaRequired();
    const captcha = await getConfirmCaptchaToken({ skip: !captchaRequired });
    if (captchaRequired && !captcha) {
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      return;
    }

    setSubmitting(true);
    crecerFormsInstance
      .submitSelfAttendanceConfirmation({
        token,
        identification: idTrimmed,
        ...(captchaRequired ? { captcha } : {}),
      })
      .then((response) => {
        setSubmitting(false);
        closePreview();
        setConfirmedIdentification(idTrimmed);
        setConfirmed(true);
        Swal.fire(
          'Registro enviado',
          response?.data?.message || 'Tu asistencia ha sido registrada.',
          'success'
        );
      })
      .catch((err) => {
        setSubmitting(false);
        closePreview();
        Swal.fire(err?.response?.data?.message || 'Error al enviar el registro.');
      });
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="report-content">
          <h1>Autoregistro de asistencia</h1>
          <p className="small">Verificando enlace...</p>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className="report-content">
          <h1>Autoregistro de asistencia</h1>
          <p className="small" style={{ color: 'rgba(255,255,255,0.9)' }}>
            {error}
          </p>
        </div>
      </PageContainer>
    );
  }

  const getStepLabel = (v) =>
    crecerSteps.find((o) => o.value === v || o.key === v)?.text ?? v ?? '-';
  const getSeminaryLabel = (v) =>
    seminaryEvent.find((o) => o.value === v || o.key === v)?.text ?? v ?? '-';

  const currentEvent = () => {
    if (info?.type === 'crecer') return crecerStepToEvent[info?.step] || null;
    if (info?.type === 'seminarios') return info?.step || null;
    return null;
  };

  const getClassDisplayLabel = (v) => {
    if (v == null || v === '') return '-';
    const list = eventDates.filter((d) => d.event === currentEvent());
    const match = list.find((d) => String(d.id) === String(v) || d.attendanceCode === v);
    if (match?.attendanceName) return match.attendanceName;
    return v;
  };

  const tz = 'America/Bogota';
  const startDate = info?.start_date ?? info?.startDate;
  const endDate = info?.end_date ?? info?.endDate;
  const singleDate = info?.date;

  const today = moment().tz(tz).startOf('day');
  const isTodayValid = (() => {
    if (startDate && endDate) {
      const start = moment(startDate).tz(tz).startOf('day');
      const end = moment(endDate).tz(tz).endOf('day');
      return today.isSameOrAfter(start, 'day') && today.isSameOrBefore(end, 'day');
    }
    if (singleDate) {
      const eventDate = moment(singleDate).tz(tz).startOf('day');
      return eventDate != null && eventDate.isSame(today, 'day');
    }
    return false;
  })();

  return (
    <PageContainer>
      <div className="report-content">
        <h1>Autoregistro de asistencia</h1>
        <p className="small">
          <small>Confirma tu asistencia ingresando tu número de identificación.</small>
        </p>

        <div className="filters-section">
          <p className="info-row title">
            {info?.type === 'crecer' ? getStepLabel(info?.step) : getSeminaryLabel(info?.step)}
          </p>
          <p className="info-row">
            <span className="info-label">Clase:</span> {getClassDisplayLabel(info?.class)}
          </p>

          {!isTodayValid ? (
            <p className="info-row" style={{ marginTop: 16, color: 'rgba(255,255,255,0.95)' }}>
              Este enlace no es válido para hoy.
            </p>
          ) : !confirmed ? (
            <form onSubmit={handleSubmit} className="identification-section">
              <div className="single-date-section">
                <label htmlFor="identification">Número de identificación</label>
                <Input
                  id="identification"
                  name="identification"
                  placeholder="Ej: 123456789"
                  value={identification}
                  onChange={handleIdentificationChange}
                  fluid
                  maxLength={20}
                />
              </div>
              <Button
                type="submit"
                secondary
                disabled={checkingIdentity || submitting || !identification.trim()}
                loading={checkingIdentity}
              >
                {checkingIdentity ? 'Verificando...' : 'Confirmar asistencia'}
              </Button>
            </form>
          ) : (
            <p className="info-row" style={{ marginTop: 16 }}>
              Tu asistencia ha sido registrada correctamente.
            </p>
          )}
        </div>

        <div>
          <p className="small">
            Puedes acceder a la información de tu asistencia en el perfil del discípulo:
          </p>
          <a
            href={
              confirmedIdentification
                ? `/perfil/crecer?identification=${confirmedIdentification}`
                : '/perfil/crecer'
            }
            target="_blank"
            rel="noreferrer"
          >
            Ver perfil del discípulo
          </a>
        </div>
      </div>

      <Modal open={previewOpen} onClose={closePreview} size="mini">
        <Modal.Header>Confirma tu identidad</Modal.Header>
        <Modal.Content>
          <p style={{ marginBottom: 8, fontSize: 14 }}>
            <strong>Documento:</strong> {identification.trim()}
          </p>
          <p style={{ marginBottom: 12, fontSize: 14 }}>
            <strong>Nombre:</strong> {previewName || '-'}
          </p>
          <p style={{ fontSize: 14 }}>
            Vamos a registrar la asistencia a <strong>{getClassDisplayLabel(info?.class)}</strong> a
            nombre de esta persona. ¿Eres tú?
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closePreview} disabled={submitting} content="Corregir" />
          <Button
            primary
            onClick={confirmAttendance}
            disabled={submitting}
            loading={submitting}
            content={submitting ? 'Registrando...' : 'Sí, soy yo'}
          />
        </Modal.Actions>
      </Modal>
    </PageContainer>
  );
};

export default CrecerSelfAttendance;
